/**
 * Glass surface panel for the learner experience.
 * The foundational container for cards, panels, and list wrappers.
 *
 * - `interactive` — darkens border + elevates shadow on hover (no translateY)
 * - `accent` — renders a coloured bar at the top edge
 * - `padding` — shorthand for consistent inner spacing (number in px)
 */
import { useState, type ReactNode, type CSSProperties } from 'react';

export interface SurfaceProps {
  children: ReactNode;
  /** Surface tone */
  tone?: 'glass' | 'solid' | 'muted';
  /** Adds hover lift and elevated shadow */
  interactive?: boolean;
  /** Colour for a top accent bar (e.g. "var(--color-info)") */
  accent?: string;
  /** Inner padding in px — not set by default so consumers control layout */
  padding?: number;
  /** Additional inline styles */
  style?: CSSProperties;
  /** Additional className */
  className?: string;
  /** Click handler (automatically sets interactive if provided) */
  onClick?: () => void;
}

const SHADOW_REST = 'var(--surface-shadow-glass-rest)';
const SHADOW_HOVER = 'var(--surface-shadow-glass-hover)';
const BORDER_HOVER = 'var(--surface-border-hover)';

const TONES: Record<
  NonNullable<SurfaceProps['tone']>,
  {
    background: string;
    border: string;
    boxShadow: string;
    backdropFilter?: string;
    webkitBackdropFilter?: string;
  }
> = {
  glass: {
    background: 'var(--color-surface)',
    border: 'var(--color-surface-border, var(--color-border))',
    boxShadow: SHADOW_REST,
    backdropFilter: 'blur(16px) saturate(1.3)',
    webkitBackdropFilter: 'blur(16px) saturate(1.3)',
  },
  solid: {
    background: 'var(--color-surface-solid)',
    border: 'var(--color-surface-border, var(--color-border))',
    boxShadow: 'var(--surface-shadow-solid)',
  },
  muted: {
    background: 'var(--color-surface-muted)',
    border: 'var(--color-surface-border, var(--color-border))',
    boxShadow: 'none',
  },
};

export function Surface({
  children,
  tone = 'glass',
  interactive,
  accent,
  padding: pad,
  style,
  className,
  onClick,
}: SurfaceProps) {
  const isInteractive = interactive || !!onClick;
  const [hovered, setHovered] = useState(false);
  const toneStyles = TONES[tone];
  const boxShadow = isInteractive && hovered ? SHADOW_HOVER : toneStyles.boxShadow;

  return (
    <div
      className={className}
      onClick={onClick}
      onMouseEnter={isInteractive ? () => setHovered(true) : undefined}
      onMouseLeave={isInteractive ? () => setHovered(false) : undefined}
      style={{
        borderRadius: 'var(--radius-lg)',
        background: toneStyles.background,
        backdropFilter: toneStyles.backdropFilter,
        WebkitBackdropFilter: toneStyles.webkitBackdropFilter,
        border: `1px solid ${isInteractive && hovered ? BORDER_HOVER : toneStyles.border}`,
        boxShadow,
        overflow: 'hidden',
        cursor: isInteractive ? 'pointer' : undefined,
        transition: isInteractive
          ? 'border-color 0.2s, box-shadow 0.2s, transform 0.2s'
          : undefined,
        transform: isInteractive && hovered ? 'scale(1.02)' : undefined,
        padding: pad,
        height: '100%',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {accent && <div style={{ height: 2, background: accent }} />}
      {children}
    </div>
  );
}
